import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {Modal, Button, Grid, Icon, Loader, Segment, Dimmer} from 'semantic-ui-react';
import {FilePond, registerPlugin} from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import {convertBase64ToFiles} from '../../../utils';
import {Actions} from '../../../../common/actions';
import {maxSizeEachFileInMegaBytes, maxSizeAllFilesInMegaBytes} from '../../../../common/constants/ftr';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import 'filepond/dist/filepond.min.css';
registerPlugin(
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
);

const maxSizeEachFile = `${maxSizeEachFileInMegaBytes}MB`;
const maxSizeAllFiles = `${maxSizeAllFilesInMegaBytes}MB`;
const maxSizeEachFileInBytes = maxSizeEachFileInMegaBytes * 1000 * 1024;
const {
  setCampaignAttachments,
  saveCampaignAttachments,
  fetchCampaignAttachments,
} = Actions;

const CampaignFiles = ({isUserTde, attachments, rawAttachments, dispatch}) => {
  const saveDowloadedData = (data, filename) => {
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style = 'display: none';
    const blob = new Blob([data], {'type': 'octet/stream'});
    const url = window.URL.createObjectURL(blob);

    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const onDownloadFile = (position) => {
    const fileInBase64 = rawAttachments[position].file;
    const filename = rawAttachments[position].name;

    const pdfs = atob(fileInBase64);
    const len = pdfs.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = pdfs.charCodeAt(i);
    }
    saveDowloadedData(bytes.buffer, filename);
  };

  const onUpdateFiles = (fileItems) => {
    const files = fileItems.map((fileItem) => {
      if (fileItem.mimetype) {
        return new URL(`data:${fileItem.mimetype};base64,${fileItem.file}`);
      }
      return fileItem.file;
    });
    dispatch(setCampaignAttachments(files));
  };

  const renderImagePreview = (fileData) => {
    const mimetype = fileData.mimetype;

    if (mimetype.includes('image')) {
      const file = fileData.file;
      return <img className='image' alt={''} src={`data:image/jpeg;base64, ${file}`} />;
    }
    return <img className='image' alt={''} src={`${process.env.PUBLIC_URL}/media/placeholder.png`} />;
  };

  if (!isUserTde) {
    return (
      <Grid textAlign='left'>
        {rawAttachments.map((fileData, index) => {
          return (
            <Grid.Row key={index}>
              <Grid.Column width={2} textAlign='right'>
                {renderImagePreview(fileData)}
              </Grid.Column>
              <Grid.Column width={10} textAlign='left'>
                <span className='link' onClick={() => onDownloadFile(index)}>
                  {fileData.name}
                </span>
              </Grid.Column>
            </Grid.Row>
          );
        })}
      </Grid>
    );
  }

  return (
    <FilePond
      allowMultiple={true}
      files={attachments}
      disabled={!isUserTde}
      labelIdle='Arrastra y suelta tus archivos o <span class="filepond--label-action">Navegador</span>'
      onupdatefiles={(fileItems) => onUpdateFiles(fileItems)}
      maxFileSize={maxSizeEachFile}
      labelMaxFileSizeExceeded='Archivo demasiado largo'
      labelMaxFileSize={`El tamaño máximo de archivo es ${maxSizeEachFile}`}
      maxTotalFileSize={maxSizeAllFiles}
      labelMaxTotalFileSizeExceeded='Tamaño total máximo excedido'
      labelMaxTotalFileSize={`El tamaño máximo total de los archivo es ${maxSizeAllFiles}`}
    />
  );
};


const ReportModal = ({
  campaingNo,
  disabled,
  isUserTde,
  attachments,
  rawAttachments,
  loadingAttachments,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [convertingFiles, setConvertingFiles] = useState(false);
  const dispatch = useDispatch();

  const convertedFiles = async() => {
    if (convertingFiles && !attachments.length && rawAttachments.length) {
      const convertedFilesResult = await convertBase64ToFiles(rawAttachments);
      dispatch(setCampaignAttachments(convertedFilesResult));
    }
    setConvertingFiles(false);
  };

  useEffect(() => {
    convertedFiles();
  }, [rawAttachments]);

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const openModal = () => {
    setModalIsOpen(true);
  };

  const fetchAttachments = () => {
    if (isUserTde) {
      setConvertingFiles(true);
    }
    dispatch(fetchCampaignAttachments(campaingNo));
    openModal();
  };

  const processFiles = () => {
    const formData = new FormData();

    attachments.forEach((element) => {
      const name = element.name;

      if (element.size < maxSizeEachFileInBytes) {
        formData.append('file', element, name);
      }
    });

    return formData;
  };

  const onSaveAttachments = () => {
    const formData = processFiles();
    dispatch(saveCampaignAttachments(campaingNo, formData));
    closeModal();
  };

  const renderContent = () => {
    if (loadingAttachments || convertingFiles) {
      return (
        <Segment basic padded='very'>
          <Dimmer active inverted>
            <Loader inverted content='Preparando adjuntos' />
          </Dimmer>
        </Segment>
      );
    } else if (!isUserTde && !rawAttachments.length) {
      return <p>No hay archivos adjuntos</p>;
    }

    return (
      <CampaignFiles
        isUserTde={isUserTde}
        rawAttachments={rawAttachments}
        attachments={attachments}
        dispatch={dispatch}
      />
    );
  };

  return (
    <Modal
      size='small'
      onOpen={fetchAttachments}
      open={modalIsOpen}
      className='report-modal'
      trigger={
        <Button disabled={disabled} color='blue'>
          <Icon name='file' />
          BOLETÍN
        </Button>
      }
    >
      <Modal.Header>Boletín de adjuntos</Modal.Header>
      <Modal.Content className='report-content'>
        {renderContent()}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={closeModal}>Cerrar</Button>
        {isUserTde && (<Button positive onClick={onSaveAttachments}>
          Guardar
        </Button>)}
      </Modal.Actions>
    </Modal>
  );
};

export default ReportModal;
